import React, { useEffect, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  Select,
  InputNumber,
  message,
  Tabs,
  Card,
  Row,
  Col,
  Popconfirm,
  Tooltip,
  DatePicker,
  Empty,
  Spin,
  Dropdown,
  MenuProps,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  MinusOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

interface EquipmentType {
  value: string;
  label: string;
}

interface Equipment {
  id: string;
  equipment_type: string;
  model: string;
  serial_number?: string;
  notes?: string;
  status: string;
  location?: string;
  created_at: string;
  updated_at: string;
  assigned_businesses: any[];
}

interface Replacement {
  id: string;
  business_id: string;
  business_name?: string;
  mid?: string;
  equipment_type: string;
  model: string;
  replaced_by?: string;
  replaced_by_user_id?: string;
  replaced_by_first_name?: string;
  replaced_by_last_name?: string;
  replacement_date: string;
  serial_number?: string;
  serial_initial?: string;
  tracking_number?: string;
  ticket_id?: string;
  ticket_number?: string;
  notes?: string;
  created_by?: string;
  created_by_first_name?: string;
  created_by_last_name?: string;
  created_at: string;
  updated_at: string;
}

interface Business {
  id: string;
  business_name: string;
  mid?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

const Equipment: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const canManageReplacements = usePermission('manage_replacements');
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [typeForm] = Form.useForm();
  const [replacementForm] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [replacementModalVisible, setReplacementModalVisible] = useState(false);
  const [replacementModalMinimized, setReplacementModalMinimized] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<EquipmentType | null>(null);
  const [editingReplacementId, setEditingReplacementId] = useState<string | null>(null);
  const [editingReplacementRecord, setEditingReplacementRecord] = useState<Replacement | null>(null);
  const [businessSearchTerm, setBusinessSearchTerm] = useState('');
  const [ticketSearchTerm, setTicketSearchTerm] = useState('');
  const [selectedEquipmentType, setSelectedEquipmentType] = useState<string | undefined>(undefined);
  const [replacementDateRange, setReplacementDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const getInitialTab = () => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const tabParam = params.get('tab');
    if (tabParam === 'types' || tabParam === 'replacements') {
      return tabParam;
    }
    return 'equipment';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab);

  // Fetch equipment
  const { data: equipmentData, isLoading } = useQuery(
    ['equipment', searchTerm],
    async () => {
      const response = await api.get('/api/equipment', {
        params: { search: searchTerm || undefined }
      });
      return response.data;
    }
  );

  const equipment = equipmentData?.equipment || [];

  // Fetch equipment types
  const { data: typesData, isLoading: typesLoading } = useQuery(
    'equipment-types',
    async () => {
      const response = await api.get('/api/equipment/types');
      return response.data;
    }
  );

  const equipmentTypes = typesData?.types || [];

  // State to store the current business being edited
  const [currentBusiness, setCurrentBusiness] = useState<Business | null>(null);

  // Fetch businesses for replacement form
  const { data: businessesData, isLoading: businessesLoading } = useQuery(
    ['businesses-search', businessSearchTerm],
    async () => {
      if (!businessSearchTerm || businessSearchTerm.length < 2) {
        return { businesses: [] };
      }
      const response = await api.get('/api/businesses/search', {
        params: { search: businessSearchTerm, limit: 50 }
      });
      return response.data;
    },
    {
      enabled: businessSearchTerm.length >= 2,
    }
  );

  // Fetch tickets for replacement form (searchable by ticket number/subject)
  const { data: replacementTicketsData, isLoading: replacementTicketsLoading } = useQuery(
    ['tickets-search', ticketSearchTerm],
    async () => {
      const response = await api.get('/api/tickets', {
        params: { search: ticketSearchTerm, limit: 30, page: 1 }
      });
      return response.data?.tickets || [];
    },
    { enabled: ticketSearchTerm.length >= 1 }
  );
  // When editing, ensure current ticket is in options if not from search
  const replacementTicketsOptions = (() => {
    const list = replacementTicketsData || [];
    const record = editingReplacementRecord;
    const ticketId = record?.ticket_id;
    const ticketNumber = record?.ticket_number;
    if (ticketId && ticketNumber && !list.some((t: any) => t.id === ticketId)) {
      return [{ id: ticketId, ticket_number: ticketNumber, subject: '' }, ...list];
    }
    return list;
  })();

  // Fetch agents/admins for "Who Replaced it" dropdown
  const { data: agentsData } = useQuery(
    'users-agents',
    async () => {
      const response = await api.get('/api/users/agents');
      return response.data?.users || [];
    }
  );
  const agentsList = agentsData || [];

  const businesses = businessesData?.businesses || [];
  
  // Include current business in the list if it exists
  const allBusinesses = currentBusiness && !businesses.find((b: Business) => b.id === currentBusiness.id)
    ? [currentBusiness, ...businesses]
    : businesses;

  // Fetch equipment models based on selected equipment type
  const { data: equipmentModelsData, isLoading: modelsLoading } = useQuery(
    ['equipment-models', selectedEquipmentType],
    async () => {
      if (!selectedEquipmentType) {
        return { models: [] };
      }
      const response = await api.get('/api/equipment', {
        params: { equipment_type: selectedEquipmentType }
      });
      // Get unique models for the selected equipment type
      const equipment = response.data?.equipment || [];
      const uniqueModels = Array.from(new Set(equipment.map((eq: Equipment) => eq.model).filter(Boolean)));
      return { models: uniqueModels.sort() };
    },
    {
      enabled: !!selectedEquipmentType,
    }
  );

  const equipmentModels = equipmentModelsData?.models || [];

  // Fetch replacements with date filter
  const { data: replacementsData, isLoading: replacementsLoading } = useQuery(
    ['equipment-replacements', replacementDateRange],
    async () => {
      const params: any = {};
      if (replacementDateRange && replacementDateRange[0] && replacementDateRange[1]) {
        params.start_date = replacementDateRange[0].format('YYYY-MM-DD');
        params.end_date = replacementDateRange[1].format('YYYY-MM-DD');
      }
      const response = await api.get('/api/equipment/replacements', { params });
      return response.data;
    }
  );

  const replacements = replacementsData?.replacements || [];

  // Create equipment mutation
  const createEquipmentMutation = useMutation(
    async (data: any) => {
      const response = await api.post('/api/equipment', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['equipment']);
        setModalVisible(false);
        form.resetFields();
        message.success('Equipment created successfully');
      },
    }
  );

  // Update equipment mutation
  const updateEquipmentMutation = useMutation(
    async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/api/equipment/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['equipment']);
        setModalVisible(false);
        setEditingId(null);
        form.resetFields();
        message.success('Equipment updated successfully');
      },
    }
  );

  // Delete equipment mutation
  const deleteEquipmentMutation = useMutation(
    async (id: string) => {
      await api.delete(`/api/equipment/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['equipment']);
        message.success('Equipment deleted successfully');
      },
    }
  );

  // Create equipment type mutation
  const createTypeMutation = useMutation(
    async (data: { value: string; label: string }) => {
      const response = await api.post('/api/equipment/types', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('equipment-types');
        setTypeModalVisible(false);
        typeForm.resetFields();
        message.success('Equipment type created successfully');
      },
    }
  );

  // Delete equipment type mutation
  const deleteTypeMutation = useMutation(
    async (value: string) => {
      await api.delete(`/api/equipment/types/${value}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('equipment-types');
        message.success('Equipment type deleted successfully');
      },
    }
  );

  // Create replacement mutation
  const createReplacementMutation = useMutation(
    async (data: any) => {
      const payload: any = {
        business_id: data.business_id,
        equipment_type: data.equipment_type,
        model: data.model,
        replacement_date: dayjs(data.replacement_date).format('YYYY-MM-DD'),
        serial_initial: data.serial_initial || undefined,
        serial_number: data.serial_number || undefined,
        tracking_number: data.tracking_number || undefined,
        ticket_id: data.ticket_id || undefined,
        replaced_by_user_id: data.replaced_by_user_id || undefined,
        notes: data.notes || undefined,
      };
      const response = await api.post('/api/equipment/replacements', payload);
      return response.data;
    },
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries('equipment-replacements');
        setReplacementModalVisible(false);
        replacementForm.resetFields();
        setBusinessSearchTerm('');
        const ticketNum = data?.replacement?.ticket_number;
        message.success(ticketNum ? `Replacement created. Ticket #${ticketNum} created.` : 'Replacement created successfully');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create replacement');
      },
    }
  );

  // Update replacement mutation
  const updateReplacementMutation = useMutation(
    async ({ id, data }: { id: string; data: any }) => {
      // Format date if it's a dayjs object
      const formattedData = { ...data };
      if (formattedData.replacement_date) {
        if (dayjs.isDayjs(formattedData.replacement_date)) {
          formattedData.replacement_date = formattedData.replacement_date.format('YYYY-MM-DD');
        } else if (typeof formattedData.replacement_date === 'string') {
          // Already a string, ensure it's in the right format
          formattedData.replacement_date = dayjs(formattedData.replacement_date).format('YYYY-MM-DD');
        }
      }
      const response = await api.put(`/api/equipment/replacements/${id}`, formattedData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('equipment-replacements');
        setReplacementModalVisible(false);
        setEditingReplacementId(null);
        replacementForm.resetFields();
        setBusinessSearchTerm('');
        message.success('Replacement updated successfully');
      },
      onError: (error: any) => {
        console.error('Update replacement error:', error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to update replacement';
        message.error(errorMessage);
      },
    }
  );

  // Delete replacement mutation
  const deleteReplacementMutation = useMutation(
    async (id: string) => {
      await api.delete(`/api/equipment/replacements/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('equipment-replacements');
        message.success('Replacement deleted successfully');
      },
    }
  );

  const handleSubmit = (values: any) => {
    if (editingId) {
      updateEquipmentMutation.mutate({ id: editingId, data: values });
    } else {
      createEquipmentMutation.mutate(values);
    }
  };

  const handleTypeSubmit = (values: { value: string; label: string }) => {
    createTypeMutation.mutate(values);
  };

  const handleEdit = (record: Equipment) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteEquipmentMutation.mutate(id);
  };

  const handleDeleteType = (value: string) => {
    deleteTypeMutation.mutate(value);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
  };

  const handleTypeModalClose = () => {
    setTypeModalVisible(false);
    setEditingType(null);
    typeForm.resetFields();
  };

  const handleReplacementSubmit = (values: any) => {
    if (editingReplacementId) {
      updateReplacementMutation.mutate({ id: editingReplacementId, data: values });
    } else {
      createReplacementMutation.mutate(values);
    }
  };

  const handleEditReplacement = async (record: Replacement) => {
    setEditingReplacementId(record.id);
    setSelectedEquipmentType(record.equipment_type);
    
    // Fetch business details if we have business_id
    if (record.business_id) {
      try {
        const response = await api.get(`/api/businesses/${record.business_id}`);
        const business = response.data.business;
        setCurrentBusiness(business);
        // Set search term to show the business name
        setBusinessSearchTerm(business.business_name || '');
      } catch (error) {
        console.error('Failed to fetch business:', error);
        // If fetch fails, try to use the business_name from the record if available
        if (record.business_name) {
          setBusinessSearchTerm(record.business_name);
        }
      }
    }
    
    replacementForm.setFieldsValue({
      ...record,
      replacement_date: dayjs(record.replacement_date),
      ticket_id: record.ticket_id || undefined,
      replaced_by_user_id: record.replaced_by_user_id || undefined,
    });
    if (record.ticket_number) setTicketSearchTerm(record.ticket_number);
    setEditingReplacementRecord(record);
    setReplacementModalVisible(true);
  };

  const handleDeleteReplacement = (id: string) => {
    deleteReplacementMutation.mutate(id);
  };

  const handleReplacementModalClose = () => {
    setReplacementModalVisible(false);
    setReplacementModalMinimized(false);
    setEditingReplacementId(null);
    setEditingReplacementRecord(null);
    replacementForm.resetFields();
    setBusinessSearchTerm('');
    setTicketSearchTerm('');
    setSelectedEquipmentType(undefined);
    setCurrentBusiness(null);
  };

  const handleReplacementMinimize = () => {
    setReplacementModalMinimized(true);
  };

  const handleReplacementRestore = () => {
    setReplacementModalMinimized(false);
  };

  const getExportData = () => {
    // Filter replacements by date range if set
    let dataToExport = replacements;
    if (replacementDateRange && replacementDateRange[0] && replacementDateRange[1]) {
      dataToExport = replacements.filter((r: Replacement) => {
        const replacementDate = dayjs(r.replacement_date);
        return replacementDate.isAfter(replacementDateRange[0]!.subtract(1, 'day')) && 
               replacementDate.isBefore(replacementDateRange[1]!.add(1, 'day'));
      });
    }

    // Prepare data for export
    const exportData = dataToExport.map((r: Replacement) => {
      const replacedByName = (r.replaced_by_first_name != null || r.replaced_by_last_name != null)
        ? [r.replaced_by_first_name, r.replaced_by_last_name].filter(Boolean).join(' ')
        : r.replaced_by || '';
      return {
        'Business Name': r.business_name || 'N/A',
        'MID': r.mid || '',
        'Ticket #': r.ticket_number ? `#${r.ticket_number}` : '',
        'Equipment Type': r.equipment_type || '',
        'Model': r.model || '',
        'Replaced By': replacedByName,
        'Replacement Date': r.replacement_date ? dayjs(r.replacement_date).format('YYYY-MM-DD') : '',
        'Serial (Initial)': r.serial_initial || '',
        'Serial (Replacement)': r.serial_number || '',
        'Tracking #': r.tracking_number || '',
        'Notes': r.notes || '',
        'Created By': r.created_by_first_name && r.created_by_last_name 
          ? `${r.created_by_first_name} ${r.created_by_last_name}` 
          : '',
        'Created At': r.created_at ? dayjs(r.created_at).format('YYYY-MM-DD HH:mm:ss') : ''
      };
    });

    return exportData;
  };

  const handleExportCSV = () => {
    const exportData = getExportData();
    
    if (exportData.length === 0) {
      message.warning('No data to export');
      return;
    }

    // Create CSV content
    const headers = ['Business Name', 'MID', 'Equipment Type', 'Model', 'Replaced By', 'Replacement Date', 'Serial Number', 'Notes', 'Created By', 'Created At'];
    const csvRows = [
      headers.join(','),
      ...exportData.map((row: any) => {
        return [
          `"${(row['Business Name'] || '').replace(/"/g, '""')}"`,
          `"${(row['MID'] || '').replace(/"/g, '""')}"`,
          `"${(row['Equipment Type'] || '').replace(/"/g, '""')}"`,
          `"${(row['Model'] || '').replace(/"/g, '""')}"`,
          `"${(row['Replaced By'] || '').replace(/"/g, '""')}"`,
          `"${(row['Replacement Date'] || '').replace(/"/g, '""')}"`,
          `"${(row['Serial Number'] || '').replace(/"/g, '""')}"`,
          `"${(row['Notes'] || '').replace(/"/g, '""')}"`,
          `"${(row['Created By'] || '').replace(/"/g, '""')}"`,
          `"${(row['Created At'] || '').replace(/"/g, '""')}"`
        ].join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const dateRangeStr = replacementDateRange && replacementDateRange[0] && replacementDateRange[1]
      ? `_${replacementDateRange[0].format('YYYY-MM-DD')}_to_${replacementDateRange[1].format('YYYY-MM-DD')}`
      : '_all';
    link.setAttribute('download', `equipment_replacements${dateRangeStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    message.success(`Exported ${exportData.length} replacement(s) to CSV`);
  };

  const handleExportExcel = () => {
    const exportData = getExportData();
    
    if (exportData.length === 0) {
      message.warning('No data to export');
      return;
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    const colWidths = [
      { wch: 20 }, // Business Name
      { wch: 15 }, // MID
      { wch: 18 }, // Equipment Type
      { wch: 20 }, // Model
      { wch: 18 }, // Replaced By
      { wch: 18 }, // Replacement Date
      { wch: 18 }, // Serial Number
      { wch: 30 }, // Notes
      { wch: 18 }, // Created By
      { wch: 20 }  // Created At
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Equipment Replacements');
    
    // Generate file name
    const dateRangeStr = replacementDateRange && replacementDateRange[0] && replacementDateRange[1]
      ? `_${replacementDateRange[0].format('YYYY-MM-DD')}_to_${replacementDateRange[1].format('YYYY-MM-DD')}`
      : '_all';
    
    // Write file
    XLSX.writeFile(wb, `equipment_replacements${dateRangeStr}.xlsx`);
    
    message.success(`Exported ${exportData.length} replacement(s) to Excel`);
  };

  const handleExportReplacements = (format: 'csv' | 'excel') => {
    if (format === 'csv') {
      handleExportCSV();
    } else {
      handleExportExcel();
    }
  };

  const equipmentColumns: ColumnsType<Equipment> = [
    {
      title: 'Type',
      dataIndex: 'equipment_type',
      key: 'equipment_type',
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const getStatusDisplay = (status: string) => {
          switch (status) {
            case 'active':
              return 'Active';
            case 'not_supported':
              return 'Not Supported';
            default:
              return status;
          }
        };
        
        return (
          <Tag color={status === 'active' ? 'green' : 'red'}>
            {getStatusDisplay(status)}
          </Tag>
        );
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this equipment?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const typeColumns: ColumnsType<EquipmentType> = [
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Are you sure you want to delete this equipment type?"
            onConfirm={() => handleDeleteType(record.value)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const replacementColumns: ColumnsType<Replacement> = [
    {
      title: 'Business',
      dataIndex: 'business_name',
      key: 'business_name',
      render: (text: string, record: Replacement) => text || 'N/A',
    },
    {
      title: 'Ticket #',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      render: (text: string) => text ? `#${text}` : '—',
    },
    {
      title: 'Equipment Type',
      dataIndex: 'equipment_type',
      key: 'equipment_type',
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Replaced By',
      key: 'replaced_by',
      render: (_: unknown, record: Replacement) => {
        if (record.replaced_by_first_name != null || record.replaced_by_last_name != null) {
          return [record.replaced_by_first_name, record.replaced_by_last_name].filter(Boolean).join(' ') || '—';
        }
        return record.replaced_by || '—';
      },
    },
    {
      title: 'Date',
      dataIndex: 'replacement_date',
      key: 'replacement_date',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Serial (Initial)',
      dataIndex: 'serial_initial',
      key: 'serial_initial',
      render: (text: string) => text || '—',
    },
    {
      title: 'Serial (Replacement)',
      dataIndex: 'serial_number',
      key: 'serial_number',
      render: (text: string) => text || '—',
    },
    {
      title: 'Tracking #',
      dataIndex: 'tracking_number',
      key: 'tracking_number',
      render: (text: string) => text || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditReplacement(record)}
            />
          </Tooltip>
          {user?.role === 'admin' && (
            <Popconfirm
              title="Are you sure you want to delete this replacement?"
              onConfirm={() => handleDeleteReplacement(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    // Only redirect away from replacements tab when auth has loaded and user definitely lacks permission.
    // While auth is loading, sync tab from URL so users with permission see the replacements tab once user loads.
    if (tabParam === 'replacements') {
      if (!authLoading && !canManageReplacements) {
        setActiveTab('equipment');
        navigate('/equipment');
        return;
      }
      setActiveTab('replacements');
      return;
    }
    if (tabParam === 'types') {
      setActiveTab('types');
    } else {
      setActiveTab('equipment');
    }
  }, [location.search, canManageReplacements, authLoading, navigate]);

  const handleTabChange = (key: string) => {
    if (key === 'replacements' && !canManageReplacements) return;
    const allowedTabs = [...(canManageReplacements ? ['replacements'] : []), 'equipment', 'types'];
    if (!allowedTabs.includes(key)) return;
    if (key === 'equipment') {
      navigate('/equipment');
    } else {
      navigate(`/equipment?tab=${key}`);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Replacement Management</Title>
      
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        {canManageReplacements && (
        <TabPane tab="Replacements" key="replacements">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Title level={4}>Equipment Replacements</Title>
              </Col>
              <Col>
                <Space>
                  <DatePicker.RangePicker
                    placeholder={['Start Date', 'End Date']}
                    value={replacementDateRange}
                    onChange={(dates) => setReplacementDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
                    format="YYYY-MM-DD"
                    style={{ width: 300 }}
                    allowClear
                  />
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'csv',
                          label: 'Export as CSV',
                          icon: <DownloadOutlined />,
                          onClick: () => handleExportReplacements('csv'),
                        },
                        {
                          key: 'excel',
                          label: 'Export as Excel',
                          icon: <DownloadOutlined />,
                          onClick: () => handleExportReplacements('excel'),
                        },
                      ],
                    }}
                  >
                    <Button icon={<DownloadOutlined />} disabled={replacements.length === 0}>
                      Export
                    </Button>
                  </Dropdown>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setReplacementModalVisible(true)}
                  >
                    Add Replacement
                  </Button>
                </Space>
              </Col>
            </Row>

            <Table
              columns={replacementColumns}
              dataSource={replacements}
              rowKey="id"
              loading={replacementsLoading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </Card>
        </TabPane>
        )}
        <TabPane tab="Equipment" key="equipment">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Input
                  placeholder="Search equipment..."
                  prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 300 }}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setModalVisible(true)}
                >
                  Add Equipment
                </Button>
              </Col>
            </Row>

            <Table
              columns={equipmentColumns}
              dataSource={equipment}
              rowKey="id"
              loading={isLoading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Equipment Types" key="types">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Title level={4}>Equipment Types</Title>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setTypeModalVisible(true)}
                >
                  Add Type
                </Button>
              </Col>
            </Row>

            <Table
              columns={typeColumns}
              dataSource={equipmentTypes}
              rowKey="value"
              loading={typesLoading}
              pagination={false}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Equipment Modal */}
      <Modal
        title={editingId ? 'Edit Equipment' : 'Add Equipment'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="equipment_type"
            label="Equipment Type"
            rules={[{ required: true, message: 'Please select equipment type' }]}
          >
            <Select placeholder="Select equipment type">
              {equipmentTypes.map((type: EquipmentType) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="model"
            label="Model"
            rules={[{ required: true, message: 'Please enter model' }]}
          >
            <Input placeholder="Enter model" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select placeholder="Select status">
              <Option value="active">Active</Option>
              <Option value="not_supported">Not Supported</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="location"
            label="Location"
          >
            <Input placeholder="Enter location" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} placeholder="Enter notes" />
          </Form.Item>

          <Form.Item>
            <Space>
          <Button 
                type="primary"
                htmlType="submit"
                loading={createEquipmentMutation.isLoading || updateEquipmentMutation.isLoading}
              >
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleModalClose}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Equipment Type Modal */}
      <Modal
        title="Add Equipment Type"
        open={typeModalVisible}
        onCancel={handleTypeModalClose}
        footer={null}
        width={400}
      >
        <Form
          form={typeForm}
          layout="vertical"
          onFinish={handleTypeSubmit}
        >
          <Form.Item
            name="value"
            label="Value"
            rules={[{ required: true, message: 'Please enter value' }]}
          >
            <Input placeholder="Enter value (e.g., pos_terminal)" />
          </Form.Item>

          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: 'Please enter label' }]}
          >
            <Input placeholder="Enter label (e.g., POS Terminal)" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createTypeMutation.isLoading}
              >
                Create
              </Button>
              <Button onClick={handleTypeModalClose}>
                Cancel
          </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Replacement Modal - can minimize to grab ticket # then restore */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginRight: 24 }}>
            <span>{editingReplacementId ? 'Edit Replacement' : 'Add Replacement'}</span>
            <Space size="small">
              <Tooltip title="Minimize">
                <Button type="text" size="small" icon={<MinusOutlined />} onClick={handleReplacementMinimize} />
              </Tooltip>
              <Tooltip title="Close">
                <Button type="text" size="small" icon={<CloseOutlined />} onClick={handleReplacementModalClose} />
              </Tooltip>
            </Space>
          </div>
        }
        closeIcon={null}
        open={replacementModalVisible && !replacementModalMinimized}
        onCancel={handleReplacementModalClose}
        footer={null}
        width={600}
      >
        <Form
          form={replacementForm}
          layout="vertical"
          onFinish={handleReplacementSubmit}
        >
          <Form.Item
            name="business_id"
            label="Business"
            rules={[{ required: true, message: 'Please select a business' }]}
          >
            <Select
              showSearch
              placeholder="Search for a business..."
              value={replacementForm.getFieldValue('business_id')}
              onSearch={(value) => setBusinessSearchTerm(value)}
              filterOption={false}
              notFoundContent={businessSearchTerm.length < 2 ? <div>Type at least 2 characters to search...</div> : (businessesLoading ? <Spin size="small" /> : <Empty description="No businesses found" />)}
              style={{ width: '100%' }}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              optionLabelProp="label"
              allowClear
            >
              {allBusinesses.map((business: Business) => {
                const addressParts = [];
                if (business.address) addressParts.push(business.address);
                if (business.city) addressParts.push(business.city);
                if (business.state) addressParts.push(business.state);
                if (business.zip_code) addressParts.push(business.zip_code);
                const fullAddress = addressParts.length > 0 ? ` - ${addressParts.join(', ')}` : '';
                const displayText = `${business.business_name}${business.mid ? ` (${business.mid})` : ''}${fullAddress}`;
                return (
                  <Option key={business.id} value={business.id} label={business.business_name} title={displayText}>
                    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={displayText}>
                      <div style={{ fontWeight: 500 }}>{business.business_name}</div>
                      {business.mid && <div style={{ fontSize: '12px', color: '#666' }}>MID: {business.mid}</div>}
                      {fullAddress && <div style={{ fontSize: '12px', color: '#999' }}>{addressParts.join(', ')}</div>}
                    </div>
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="equipment_type"
            label="Equipment Type"
            rules={[{ required: true, message: 'Please select equipment type' }]}
          >
            <Select 
              placeholder="Select equipment type"
              onChange={(value) => {
                // Clear model when equipment type changes and update state
                setSelectedEquipmentType(value);
                replacementForm.setFieldsValue({ model: undefined, equipment_type: value });
              }}
            >
              {equipmentTypes.map((type: EquipmentType) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="model"
            label="Model"
            rules={[{ required: true, message: 'Please select model' }]}
          >
            <Select 
              placeholder={selectedEquipmentType ? (equipmentModels.length === 0 ? "No models found for this type" : "Select model") : "Select equipment type first"}
              disabled={!selectedEquipmentType}
              loading={modelsLoading}
              showSearch
              filterOption={(input, option) => {
                const value = String(option?.value || '');
                return value.toLowerCase().includes(input.toLowerCase());
              }}
              notFoundContent={modelsLoading ? <Spin size="small" /> : <Empty description="No models found" />}
            >
              {(equipmentModels as string[]).map((model: string) => (
                <Option key={model} value={model}>
                  {model}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ticket_id"
            label={editingReplacementId ? 'Ticket Number' : 'Support Ticket (optional)'}
            extra={!editingReplacementId ? 'Link to an existing ticket or leave blank to create a new RP ticket automatically.' : undefined}
          >
            <Select
              showSearch
              placeholder="Search by ticket # or subject..."
              value={replacementForm.getFieldValue('ticket_id')}
              onSearch={(value) => setTicketSearchTerm(value)}
              filterOption={false}
              notFoundContent={ticketSearchTerm.length < 1 ? <div>Type to search tickets...</div> : (replacementTicketsLoading ? <Spin size="small" /> : <Empty description="No tickets found" />)}
              style={{ width: '100%' }}
              dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
              allowClear
              optionLabelProp="label"
            >
              {replacementTicketsOptions.map((t: any) => (
                <Option key={t.id} value={t.id} label={`#${t.ticket_number}`} title={t.subject}>
                  <div style={{ whiteSpace: 'normal' }}>
                    <span style={{ fontWeight: 500 }}>#{t.ticket_number}</span>
                    {t.subject && <span style={{ color: '#666', marginLeft: 8 }}>{t.subject.length > 50 ? t.subject.slice(0, 50) + '…' : t.subject}</span>}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="replaced_by_user_id"
            label="Who Replaced It"
            rules={[{ required: true, message: 'Please select who replaced it' }]}
          >
            <Select
              showSearch
              placeholder="Search and select admin or agent..."
              filterOption={(input, option) => {
                const label = (option?.label ?? '') as string;
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="label"
              style={{ width: '100%' }}
              dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
              allowClear
              optionLabelProp="label"
              options={agentsList.map((a: any) => ({
                value: a.id,
                label: `${a.first_name} ${a.last_name}${a.role ? ` (${a.role})` : ''}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="replacement_date"
            label="Date of Replacement"
            rules={[{ required: true, message: 'Please select replacement date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item
            name="serial_initial"
            label="Serial: Initial device"
          >
            <Input placeholder="Initial device serial number" />
          </Form.Item>

          <Form.Item
            name="serial_number"
            label="Serial: Replacement device"
          >
            <Input placeholder="Replacement device serial number" />
          </Form.Item>

          <Form.Item
            name="tracking_number"
            label="Tracking number"
          >
            <Input placeholder="Tracking number" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} placeholder="Enter any additional notes" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createReplacementMutation.isLoading || updateReplacementMutation.isLoading}
              >
                {editingReplacementId ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleReplacementModalClose}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Minimized replacement bar - click Restore to bring modal back */}
      {replacementModalVisible && replacementModalMinimized && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            background: '#1890ff',
            color: '#fff',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <span style={{ fontWeight: 500 }}>
            {editingReplacementId ? 'Edit Replacement' : 'Add Replacement'} (minimized)
          </span>
          <Button type="primary" size="small" ghost onClick={handleReplacementRestore} style={{ color: '#fff', borderColor: '#fff' }}>
            Restore
          </Button>
          <Button type="text" size="small" onClick={handleReplacementModalClose} style={{ color: 'rgba(255,255,255,0.85)' }}>
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

export default Equipment;